import type { Paidol } from 'API';
import { ErpSystem } from 'app/services/erp-connection/erpConstants';

/**
 * The paidol's source-system slug, paidol-first.
 *
 * `fallbackSlug` exists because some paidols carry no `agaveSourceSystemSlug`
 * of their own — the slug lives only on the live `agaveAccount`, and
 * AppSettings lazily backfills it. The visibility gates (useAgaveSourceSystem)
 * fall back to the account slug, so without the same fallback here those
 * paidols see the toggle but flipping it does nothing. Paidol-first
 * precedence, matching useAgaveSourceSystem.
 */
const sourceSlug = (paidol: Paidol | undefined | null, fallbackSlug?: string | null): string | null =>
  paidol?.agaveSourceSystemSlug ?? fallbackSlug ?? null;

/**
 * SEE-1622: ERPs whose stored "code" is not one a user would recognise — QBO and
 * QBD codes are internal list/record ids, and QBO GL numbers are blank. These
 * paidols are name-ordered only and never see the toggle. Mirrors the backend's
 * `_dropdown_sort.NO_CODE_SORT_SOURCE_SYSTEMS`.
 */
const NO_CODE_SORT_SOURCE_SYSTEMS: ReadonlySet<string> = new Set([
  ErpSystem.QUICKBOOKS_ONLINE,
  ErpSystem.QUICKBOOKS_DESKTOP,
]);

/**
 * SEE-1622: does the server order this paidol's coding dropdowns before
 * paginating? True for every direct-ERP paidol — the twin of the backend's
 * `_dropdown_sort.is_server_sorted`, and the two must agree: when it is true the
 * store keeps pages verbatim instead of re-sorting them.
 *
 * Both conditions are required. `erpSpeedchain` alone is too broad — prod has
 * direct-flagged paidols with no ERP connected — and the slug alone would let
 * Agave-backed paidols through.
 */
export const isServerSortedPaidol = (
  paidol: Paidol | undefined | null,
  fallbackSlug?: string | null
): boolean => Boolean(paidol?.erpSpeedchain && sourceSlug(paidol, fallbackSlug));

/**
 * SEE-1622: server-sorted, and on an ERP whose codes are meaningful to order by.
 * Gates the "sort by code" toggle's visibility, and whether vendors are labelled
 * with their vendor code (dropdown, selected value and filter panel alike).
 */
export const supportsCodeSort = (
  paidol: Paidol | undefined | null,
  fallbackSlug?: string | null
): boolean => {
  const slug = sourceSlug(paidol, fallbackSlug);
  return isServerSortedPaidol(paidol, fallbackSlug) && !NO_CODE_SORT_SOURCE_SYSTEMS.has(slug ?? '');
};

/**
 * SEE-1325: reads the paidol's "order coding dropdowns by code" toggle.
 *
 * One accessor rather than the property read inline, because the label
 * formatters, the coding thunks and the dropdown hook all branch on it, and
 * they must agree — a list ordered by code but labelled "name - (code)" reads
 * as unsorted. A QuickBooks paidol with the setting stored is still false: the
 * toggle is hidden for it, and its codes are internal ids.
 */
export const isCodingSortedByCode = (
  paidol: Paidol | undefined | null,
  fallbackSlug?: string | null
): boolean =>
  Boolean(supportsCodeSort(paidol, fallbackSlug) && paidol?.erpDropdownSortByCodeSetting?.isEnabled);
